/**
 * Booking Form Page Component
 * /admin/bookings/new and /admin/bookings/:id/edit
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiClient from '../api/client';

const BookingForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pricingPlans, setPricingPlans] = useState([]);
  const [vehicles, setVehicles] = useState([]);

  const [formData, setFormData] = useState({
    service_type: 'limousine',
    customer_type: 'direct',
    customer_id: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    vehicle: '',
    pricing_plan: '',
    trip_type: 'on_demand',
    // Journey direction, separate from trip_type which is about scheduling.
    // See ADR-0003 D-02.
    journey_type: 'one_way',
    start_datetime: '',
    end_datetime: '',
    pickup_location: '',
    dropoff_location: '',
    return_pickup_location: '',
    return_datetime: '',
    return_dropoff_location: '',
    waiting_hours: '',
    estimated_distance: '',
    estimated_cost: '',
    status: 'pending',
    notes: '',
  });
  // Rate used for the on-screen preview only. The backend recomputes and is
  // authoritative - ADR-0003 D-06 - so a stale value here cannot mis-bill.
  const [waitingRate, setWaitingRate] = useState(50);

  useEffect(() => {
    if (isEdit) {
      loadBooking();
    }
    loadPricingPlans();
    loadVehicles();
  }, [id]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getBooking(id);
      
      // Format datetime for input fields (YYYY-MM-DDTHH:mm)
      const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      setFormData({
        service_type: data.service_type || 'limousine',
        customer_type: data.customer_type || 'direct',
        customer_id: data.customer_id || '',
        customer_name: data.customer_name || '',
        customer_email: data.customer_email || '',
        customer_phone: data.customer_phone || '',
        vehicle: data.vehicle || '',
        pricing_plan: data.pricing_plan || '',
        trip_type: data.trip_type || 'on_demand',
        journey_type: data.journey_type || 'one_way',
        start_datetime: formatDateTime(data.start_datetime),
        end_datetime: formatDateTime(data.end_datetime),
        pickup_location: data.pickup_location || '',
        dropoff_location: data.dropoff_location || '',
        return_pickup_location: data.return_pickup_location || '',
        return_datetime: formatDateTime(data.return_datetime),
        return_dropoff_location: data.return_dropoff_location || '',
        waiting_hours: data.waiting_hours != null ? data.waiting_hours : '',
        estimated_distance: data.estimated_distance || '',
        estimated_cost: data.estimated_cost || '',
        status: data.status || 'pending',
        notes: data.notes || '',
      });
    } catch (err) {
      setError(err.message || 'Failed to load booking');
    } finally {
      setLoading(false);
    }
  };

  const loadPricingPlans = async () => {
    try {
      const response = await apiClient.getPricingPlans();
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      // Inactive plans stay out of the picker; an existing booking that points
      // at one still loads, it simply cannot be re-selected.
      setPricingPlans(data.filter((plan) => plan.is_active !== false));
    } catch (err) {
      console.error('Failed to load pricing plans:', err);
      setPricingPlans([]);
    }
  };

  const loadVehicles = async () => {
    try {
      const response = await apiClient.getVehicles({ status: 'active' });
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setVehicles(data);
    } catch (err) {
      console.error('Failed to load vehicles:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      // Switching to a round trip prefills the return leg as the reverse of the
      // outbound one, purely as a convenience. Both stay editable: not every
      // return route is an exact reversal.
      if (name === 'journey_type' && value === 'round_trip') {
        if (!prev.return_pickup_location) {
          next.return_pickup_location = prev.dropoff_location || '';
        }
        if (!prev.return_dropoff_location) {
          next.return_dropoff_location = prev.pickup_location || '';
        }
        if (prev.waiting_hours === '') next.waiting_hours = '0';
      }
      return next;
    });
  };

  // Mirrors the backend rules in BookingSerializer._validate_journey so the
  // operator gets immediate feedback. The backend remains authoritative -
  // this is a convenience, not the gate (ADR-0003 D-06).
  const journeyErrors = () => {
    if (formData.journey_type !== 'round_trip') return {};
    const errs = {};
    if (!formData.return_pickup_location) errs.return_pickup_location = 'Required for a round trip';
    if (!formData.return_dropoff_location) errs.return_dropoff_location = 'Required for a round trip';
    if (!formData.return_datetime) errs.return_datetime = 'Required for a round trip';
    if (formData.waiting_hours === '' || formData.waiting_hours === null) {
      errs.waiting_hours = 'Required for a round trip';
    } else if (Number(formData.waiting_hours) < 0) {
      errs.waiting_hours = 'Cannot be negative';
    }
    if (
      formData.start_datetime &&
      formData.return_datetime &&
      new Date(formData.return_datetime) <= new Date(formData.start_datetime)
    ) {
      errs.return_datetime = 'Must be later than the outbound pickup time';
    }
    return errs;
  };

  const errs = journeyErrors();
  const isRoundTrip = formData.journey_type === 'round_trip';
  const basePrice = Number(formData.estimated_cost || 0);
  const waitingHours = Number(formData.waiting_hours || 0);
  const waitingCharge = isRoundTrip && waitingHours > 0 ? waitingHours * waitingRate : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const blocking = journeyErrors();
    if (Object.keys(blocking).length > 0) {
      setError('Please complete the return journey details.');
      setSaving(false);
      return;
    }

    try {
      const submitData = {
        ...formData,
        estimated_distance: formData.estimated_distance ? parseFloat(formData.estimated_distance) : null,
        estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
        customer_id: formData.customer_type === 'corporate' && formData.customer_id ? formData.customer_id : null,
        vehicle: formData.vehicle || null,
        pricing_plan: formData.pricing_plan || null,
      };

      // Never send a total or a rate: those are the backend's to decide, and
      // it ignores them anyway. Sending them would only invite the impression
      // that the client sets prices.
      delete submitData.total_price;
      delete submitData.waiting_charge;
      delete submitData.waiting_hourly_rate;
      delete submitData.price_breakdown;

      if (formData.journey_type === 'round_trip') {
        submitData.waiting_hours = formData.waiting_hours === '' ? '0' : String(formData.waiting_hours);
      } else {
        // Do not let hidden stale values ride along on a one-way booking.
        submitData.return_pickup_location = null;
        submitData.return_datetime = null;
        submitData.return_dropoff_location = null;
        submitData.waiting_hours = '0';
      }

      if (isEdit) {
        await apiClient.updateBooking(id, submitData);
      } else {
        await apiClient.createBooking(submitData);
      }

      navigate('/admin/bookings');
    } catch (err) {
      setError(err.message || 'Failed to save booking');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/bookings')}>
            Back
          </Button>
          <Typography variant="h4" component="h1">
            {isEdit ? 'Edit Booking' : 'Add New Booking'}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Booking Information
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Service Type"
                name="service_type"
                value={formData.service_type}
                onChange={handleChange}
                select
                required
              >
                <MenuItem value="rental">Rental (Rent-a-Car)</MenuItem>
                <MenuItem value="limousine">Limousine Service</MenuItem>
                <MenuItem value="equipment">Equipment Rental</MenuItem>
                <MenuItem value="school">School Transport</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                select
                required
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Customer Type"
                name="customer_type"
                value={formData.customer_type}
                onChange={handleChange}
                select
                required
              >
                <MenuItem value="direct">Direct</MenuItem>
                <MenuItem value="corporate">Corporate</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Trip Type"
                name="trip_type"
                value={formData.trip_type}
                onChange={handleChange}
                select
                required
              >
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="on_demand">On Demand</MenuItem>
                <MenuItem value="recurring">Recurring</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Journey Type"
                name="journey_type"
                value={formData.journey_type}
                onChange={handleChange}
                select
                required
                helperText={
                  isRoundTrip
                    ? 'One booking covers both legs, with one vehicle'
                    : 'Outbound only'
                }
              >
                <MenuItem value="one_way">One Way</MenuItem>
                <MenuItem value="round_trip">Round Trip</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Customer Name"
                name="customer_name"
                value={formData.customer_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Customer Email"
                name="customer_email"
                type="email"
                value={formData.customer_email}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Customer Phone"
                name="customer_phone"
                value={formData.customer_phone}
                onChange={handleChange}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Vehicle"
                name="vehicle"
                value={formData.vehicle}
                onChange={handleChange}
                select
              >
                <MenuItem value="">None</MenuItem>
                {vehicles.map((vehicle) => (
                  <MenuItem key={vehicle.id} value={vehicle.id}>
                    {vehicle.plate_number} - {vehicle.make} {vehicle.model}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              {/* BookingSerializer requires pricing_plan, and Booking.pricing_plan
                  is null=False. The form submitted the field but never offered a
                  way to choose one, so every save returned
                  400 {"pricing_plan":["This field is required."]}. */}
              <TextField
                fullWidth
                label="Pricing Plan"
                name="pricing_plan"
                value={formData.pricing_plan}
                onChange={handleChange}
                select
                required
                error={Boolean(error) && !formData.pricing_plan}
                helperText={
                  pricingPlans.length === 0
                    ? 'No pricing plans available - create one before adding a booking'
                    : 'Required'
                }
              >
                {pricingPlans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.name}
                    {plan.base_rate !== undefined && plan.base_rate !== null
                      ? ` - ${plan.base_rate}${plan.unit ? ` / ${plan.unit}` : ''}`
                      : ''}
                    {plan.service_type ? ` (${plan.service_type})` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Start Date & Time"
                name="start_datetime"
                type="datetime-local"
                value={formData.start_datetime}
                onChange={handleChange}
                required
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="End Date & Time"
                name="end_datetime"
                type="datetime-local"
                value={formData.end_datetime}
                onChange={handleChange}
                required
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            {/* Locations are labelled "Outbound"/"Return" throughout so the two
                sets can never be mistaken for one another. */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
                Outbound Journey
              </Typography>
              <Divider sx={{ mb: 1 }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Outbound Pickup Location"
                name="pickup_location"
                value={formData.pickup_location}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Outbound Dropoff Location"
                name="dropoff_location"
                value={formData.dropoff_location}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>

            {isRoundTrip && (
              <>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>
                    Return Journey
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Part of this same booking. One vehicle covers both legs.
                    Prefilled as the reverse of the outbound leg — edit if the
                    return route differs.
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Return Pickup Location"
                    name="return_pickup_location"
                    value={formData.return_pickup_location}
                    onChange={handleChange}
                    multiline
                    rows={2}
                    required
                    error={Boolean(errs.return_pickup_location)}
                    helperText={errs.return_pickup_location}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Return Dropoff Location"
                    name="return_dropoff_location"
                    value={formData.return_dropoff_location}
                    onChange={handleChange}
                    multiline
                    rows={2}
                    required
                    error={Boolean(errs.return_dropoff_location)}
                    helperText={errs.return_dropoff_location}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Return Pickup Date & Time"
                    name="return_datetime"
                    type="datetime-local"
                    value={formData.return_datetime}
                    onChange={handleChange}
                    required
                    error={Boolean(errs.return_datetime)}
                    helperText={errs.return_datetime}
                    // slotProps, not InputLabelProps: MUI 9 removed the latter
                    // and ignores it silently, leaving the label on top of the
                    // native placeholder.
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Waiting Hours"
                    name="waiting_hours"
                    type="number"
                    value={formData.waiting_hours}
                    onChange={handleChange}
                    required
                    inputProps={{ min: 0, step: 0.5 }}
                    error={Boolean(errs.waiting_hours)}
                    helperText={
                      errs.waiting_hours ||
                      `Idle time between legs. Charged at ${waitingRate} per hour.`
                    }
                  />
                </Grid>
              </>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Estimated Distance (km)"
                name="estimated_distance"
                type="number"
                value={formData.estimated_distance}
                onChange={handleChange}
                slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={isRoundTrip ? 'Round-trip Price' : 'Estimated Cost'}
                name="estimated_cost"
                type="number"
                value={formData.estimated_cost}
                onChange={handleChange}
                slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                helperText={
                  isRoundTrip
                    ? 'Covers both legs. Not doubled for the return.'
                    : undefined
                }
              />
            </Grid>

            {isRoundTrip && (
              <Grid size={{ xs: 12 }}>
                {/* Preview only. The backend recalculates and is the source of
                    truth for the amount actually stored - ADR-0003 D-06. */}
                <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Price calculation
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Round-trip price</Typography>
                    <Typography variant="body2">{basePrice.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">
                      Waiting charge ({waitingHours || 0} h &times; {waitingRate})
                    </Typography>
                    <Typography variant="body2">{waitingCharge.toFixed(2)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle2">Total booking price</Typography>
                    <Typography variant="subtitle2">
                      {(basePrice + waitingCharge).toFixed(2)}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Confirmed by the server on save.
                  </Typography>
                </Paper>
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={4}
              />
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/admin/bookings')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Booking'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default BookingForm;

